import React, { useState, useEffect, useContext, useReducer, useMemo } from "react"
import moment, { Moment } from "moment"
import DiscountForm from "./DiscountForm"
import { CampProps, CampingStyleProps, SiteProps } from "../../interfaces"
import CampDiscountProps from "../../interfaces/CampDiscountProps"
import { BookerContext } from "~/contexts/BookerContext"
import { PriceDetails as IPriceDetails } from "../../utils/priceDetailsFor"
import { Transition, Dialog } from "@headlessui/react"
import AddOns from "../AddOns/AddOns"
import CheckoutForm from "./CheckoutForm"
import { useStripe, useElements } from "@stripe/react-stripe-js"
import api from "../../api"
import { UserProps } from "../../interfaces"
import * as Sentry from "@sentry/react"
import api2 from "../../api2"
import { UpsertData } from "~/api2/payment_intents"
import { discountType, PAYMENT_MODES } from "~/constants"
import { post } from "~/api2/utils/requests"
import DiscountProps from "../../interfaces/DiscountProps"
import CampingStyleDiscountProps from "../../interfaces/CampingStyleDiscountProps"
import CouponForm from "./CouponForm"
import OrderSummary from "../Admin/Orders/OrderSummary"
import IOrder from "~/interfaces/IOrder"
import { SENTRY_IGNORABLE_STRIPE_ERRORS } from "~/constants/stripe_ignore_sentry_codes"
import PaymentAnimation from "../ui/PaymentAnimation"
import Spinner from "../ui/Spinner"
declare let gtag: Function
interface ComponentProps {
  camp: CampProps
  selectedSite: SiteProps
  nights: number
  reservationStartDate: Moment
  reservationEndDate: Moment
  discountType: any
  setDiscountType: React.Dispatch<React.SetStateAction<any>>
  discountId: string | undefined
  setDiscountId: React.Dispatch<React.SetStateAction<string | undefined>>
  userPassengers: number
  userKidCampers: number
  stripePromise
  site: SiteProps
  setIsPaying
  userRigType
  userRigLength
  userElectric
  onError
  order: IOrder
  priceDetails: IPriceDetails
  setPriceDetails: (v: IPriceDetails) => void
  discountValid
  setDiscountValid
  discountRates
  fetchPriceDetails
  isLoading
  setIsLoading
  errorMessage
  setErrorMessage
  userParams
  userParamsDispatch
}

const flattenNotesFrom = (notesAndCustomAnswers: Record<string, string>) => {
  const notes = notesAndCustomAnswers["notes"]
  delete notesAndCustomAnswers["notes"]

  const customAnswers = Object.keys(notesAndCustomAnswers).map((key, idx) => {
    return `<h3>${key}</h3><p>${notesAndCustomAnswers[key]}</p>`
  })

  return [notes, ...customAnswers].join("<br/><br/>").replace(/^<br\/><br\/>/, "")
}

const PriceDetails = (props: ComponentProps) => {
  const {
    campingStyle: bookerCampingStyle,
    selectedAddOns,
    setSelectedAddOns,
    selectedInterval,
    userPets,
    groupedReservationId,
    couponCode,
    camp,
    startDate,
    endDate,
    userRigYear,
    trackForCamp,
    trackForPark,
    trackAdConversionForCamp,
  } = useContext(BookerContext)
  const [processing, setProcessing] = useState(false)
  const [disabled, setDisabled] = useState(true)
  const [isLoadingCamper, setIsLoadingCamper] = useState<boolean>(false)
  const [isBlocked, setIsBlocked] = useState<boolean>(false)
  const stripe = useStripe()
  const elements = useElements()
  const [intentId, setIntentId] = useState("")
  const [notesAndCustomAnswers, setNotesAndCustomAnswers] = useState({})
  const [isLoadingIntent, setIsLoadingIntent] = useState(false)
  const [couponsCount, setCouponsCount] = useState(0)
  const [stripeSuccessCount, setStripeSuccessCount] = useState(0)
  const [showSuccess, setShowSuccess] = useState(false)
  const [redirectTimeout, setRedirectTimeout] = useState<NodeJS.Timeout | null>(null)
  const [reservationId, setReservationId] = useState<string | null>(null)

  const queryParams = new URLSearchParams(decodeURIComponent(window.location.search))
  const fromAdParam = queryParams.get("from_ad")
  const [fromAd, setFromAd] = useState(false)
  const { userParams, userParamsDispatch } = props

  const selectedSiteCampingStyle =
    bookerCampingStyle || props.camp?.camping_styles?.find((style) => style.id === props.selectedSite?.camping_style_id)
  const campingStyle = groupedReservationId ? selectedSiteCampingStyle : bookerCampingStyle

  const isDisablingError = () => {
    try {
      if (!props.errorMessage || typeof props.errorMessage.includes != "function") return false

      return props.errorMessage.includes("Try again") ? false : !!props.errorMessage
    } catch (err) {
      Sentry.captureException(err, { contexts: { info: { errorMessage: props.errorMessage } } })
      return false
    }
  }

  const selectedCampingStyle = useMemo(() => {
    if (groupedReservationId) {
      return camp?.camping_styles?.find((cs) => cs.id == props.site?.camping_style_id)
    } else {
      return campingStyle
    }
  }, [camp, props.site?.camping_style_id, campingStyle, groupedReservationId])

  const canUseDiscount = (camp: CampProps, nights: number) => {
    if (camp.max_discounted_nights) {
      return nights <= camp.max_discounted_nights
    } else {
      return true
    }
  }

  useEffect(() => {
    api2.Coupons.fetchForCamp(props.camp.id) // TODO add camping style id in settings table to support this
      .then((res) => setCouponsCount(res.coupons.length))
      .catch((err) => {
        console.error(err)
      })
  }, [])

  useEffect(() => {
    let possibleDiscount: CampDiscountProps | CampingStyleDiscountProps = props.discountRates[props.discountType]
    const requiresDiscountId: boolean = possibleDiscount?.require_membership_id || false

    if (requiresDiscountId) {
      if (props.discountId?.length > 6) {
        props.setIsLoading(true)
        props.fetchPriceDetails()
        props.setDiscountValid(true)
      } else {
        props.setDiscountValid(false)
      }
    } else {
      if (props.discountType) {
        props.setIsLoading(true)
        props.fetchPriceDetails()
        props.setDiscountValid(true)
      } else {
        props.setIsLoading(true)
        props.fetchPriceDetails()
        props.setDiscountValid(false)
      }
    }
  }, [props.priceDetails?.subtotal, props.discountType, props.discountId, couponCode])

  useEffect(() => {
    setSelectedAddOns([])
  }, [props.selectedSite])

  // TODO: format this better...
  if (!props.priceDetails) {
    return <>Loading...</>
  }

  const discountDataForUpsert = (): {
    discount_type?: discountType
    discount_membership_id?: string
  } => {
    const discountSlug = props.discountType
    const memberId = props.discountId
    let discountParams = {}

    const csDiscount = selectedCampingStyle.discounts.find((discount) => discount.slug == discountSlug)
    const cDiscount = props.camp.discounts.find((discount) => discount.slug == discountSlug)

    let discount: DiscountProps
    let joinDiscount: CampDiscountProps | CampingStyleDiscountProps

    if (csDiscount) {
      discount = csDiscount
      joinDiscount = selectedCampingStyle.camping_style_discounts.find((cd) => cd.discount_id == csDiscount.id)
    } else if (cDiscount) {
      discount = cDiscount
      joinDiscount = props.camp.camp_discounts.find((cd) => cd.discount_id == cDiscount.id)
    }

    if (discount && joinDiscount) {
      if (joinDiscount.require_membership_id) {
        if (memberId) {
          discountParams = {
            discount_type: discountSlug,
            discount_slug: discountSlug,
            discount_membership_id: memberId,
          }
        } else {
          return {}
        }
      } else {
        discountParams = {
          discount_type: discountSlug,
          discount_slug: discountSlug,
          discount_membership_id: null,
        }
      }
    }

    return discountParams
  }

  const buildPriceDetailParams = () => ({
    id: intentId,
    payment_mode: PAYMENT_MODES.ONLINE,
    camping_style_id: selectedCampingStyle.id,
    site_id: props.site?.id,
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
    user_electric: props.userElectric,
    user_passengers: props.userPassengers,
    user_kid_campers: props.userKidCampers,
    number_of_pets: userPets,
    user_rig_year: userRigYear,
    interval_id: selectedInterval?.id,
    add_ons: selectedAddOns,
    is_storing_credit_card_enabled: userParams.is_saving_payment_method ?? false,
    // NOTE: these should be on by default
    is_service_fee_applied: true,
    is_tax_enabled: true,
    is_passenger_fees_enabled: true,
    is_pets_fee_enabled: true,
    is_holiday_rate_enabled: true,
    is_weekend_fee_enabled: true,
    is_credit_card_fee_enabled: true,
    coupon_codes: [couponCode],
    ...discountDataForUpsert(),
  })

  const handleSubmit = async (event: React.FormEvent) => {
    let reservation

    try {
      event.preventDefault()
      setProcessing(true)
      if (!stripe || !elements) {
        console.error("[STRIPE] stripe or elements not loaded")
        props.setErrorMessage(
          "There's an issue loading the Stripe payment processor. Please reload the page and try again.",
        )
        setProcessing(false)
        return
      }

      const pdParams = buildPriceDetailParams()

      const { price_detail: pd } = (await post(`/sites/${props.site?.id}/price_details`, pdParams)) as {
        price_detail: IPriceDetails
      }
      const addedSiteIds = pd.additional_fees
        ?.filter((fee) => fee.type == "camping-style")
        .flatMap((fee) => fee.site_ids)

      const { id: intentId, clientSecret } = await upsertIntent(pdParams)
      reservation = await createReservation(intentId, pdParams, addedSiteIds as Array<string>)

      props.setErrorMessage("")

      const { error: submitError } = await elements.submit()
      if (submitError) {
        throw new Error(submitError.message)
      }

      const payload = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/reservations/${reservation.id}`,
        },
        redirect: "if_required",
      })

      if (payload.error) {
        let stripeMessage = payload.error.message

        if (payload.error.code === "processing_error") {
          stripeMessage += ` If you continue to experience issues, please give us a call at ${props.camp.phone} to book directly.`
          throw new Error(stripeMessage)
        }
      
        if (SENTRY_IGNORABLE_STRIPE_ERRORS.includes(payload.error.code)) {
          const err = new Error(stripeMessage)
          err.code = payload.error.code
          throw err
        }

        throw new Error(stripeMessage)
      }

      setStripeSuccessCount((prevValue) => prevValue + 1)
      props.setErrorMessage("")
      trackForPark("purchase", { value: pd.total, currency: camp.currency, camp: camp.name })

      if (fromAd || fromAdParam) {
        trackForCamp("purchase_ad", { value: pd.total, currency: camp.currency })
        trackAdConversionForCamp("conversion", { value: pd.total, currency: camp.currency })
      }
      trackForCamp("purchase", { value: pd.total, currency: camp.currency })

      setReservationId(reservation.id)
      setShowSuccess(true)
      const timeout = setTimeout(() => {
        window.location.href = `/reservations/${reservation.id}`
      }, 3000)
      setRedirectTimeout(timeout)
    } catch (err) {
      if (!(err && SENTRY_IGNORABLE_STRIPE_ERRORS.includes(err.code))) {
        Sentry.captureException(err)
      }
    
      setDisabled(true)
      if ((err as { message: string } | undefined)?.message) {
        props.setErrorMessage(err?.message)
        props.onError(err?.message)
      }
      setProcessing(false)
    }
  }

  const upsertIntent = async (data: UpsertData) => {
    setIsLoadingIntent(true)
    const intent: { id: string; clientSecret: string } = await api2.PaymentIntents.upsert({
      ...data,
      ...(userParams as UserProps),
    })

    if (!intent.clientSecret) throw new Error("Missing client secret from payment intent response")

    setIntentId(intent.id)
    setIsLoadingIntent(false)
    return { id: intent.id, clientSecret: intent.clientSecret }
  }

  const createReservation = async (paymentIntentId: string, pdParams: any, addedSiteIds: string[]) =>
    api.createReservation(
      paymentIntentId,
      props.site?.id,
      selectedCampingStyle.id,
      startDate.toISOString(),
      endDate.toISOString(),
      props.userPassengers,
      props.userKidCampers,
      userPets || userParams.pets_info,
      // TODO: pass in userPassengers as reservation#passengers field
      userParams as UserProps, // TODO: migrate this to ICamper
      selectedInterval?.id,
      groupedReservationId,
      flattenNotesFrom(notesAndCustomAnswers),
      pdParams,
      addedSiteIds,
      true,
    )

  const taxRateLabel = campingStyle?.tax_rate ? campingStyle?.tax_rate_label : camp?.tax_rate ? camp?.tax_rate_label : "Tax"

  useEffect(() => {
    return () => {
      if (redirectTimeout) clearTimeout(redirectTimeout)
    }
  }, [redirectTimeout])

  return (
    <div className="relative space-y-6 mt-2">
      <Transition
        show={props.isLoading}
        enter="transition-opacity ease-out duration-75"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-in duration-75"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="absolute w-full h-full z-30 bg-white bg-opacity-80 flex items-center justify-center text-sm">
          <Spinner size={5} />
          Loading...
        </div>
      </Transition>
      <Transition show={showSuccess}>
        <Dialog open={showSuccess} onClose={() => {}} className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-40" />
          <div className="relative bg-white rounded-lg shadow-lg p-8 flex flex-col items-center max-w-lg mx-auto">
            <PaymentAnimation type={"success"} />
            <h2 className="text-2xl font-bold mb-2 text-green-700">Payment successful!</h2>
            <p className="mb-4 text-center text-gray-700">
              Click the button below to view your confirmation, or you’ll be automatically redirected there in a few
              seconds.
            </p>
            <button
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
              onClick={() => {
                if (redirectTimeout) clearTimeout(redirectTimeout)
                if (reservationId) window.location.href = `/reservations/${reservationId}`
              }}
            >
              View My Reservation
            </button>
          </div>
        </Dialog>
      </Transition>
      <div className="flex justify-between gap-10 flex-col-reverse md:flex-row">
        <div style={{ flex: "0 1 50%" }}>
          <CheckoutForm
            stripeSuccessCount={stripeSuccessCount}
            userParams={userParams}
            userParamsDispatch={userParamsDispatch}
            camp={camp}
            site={props.site}
            startDate={startDate}
            endDate={endDate}
            setIsPaying={props.setIsPaying}
            discountType={props.discountType}
            discountId={props.discountId}
            userRigType={props.userRigType}
            userRigLength={props.userRigLength}
            userElectric={props.userElectric}
            userPassengers={props.userPassengers}
            userKidCampers={props.userKidCampers}
            onError={props.onError}
            setIsLoadingCamper={setIsLoadingCamper}
            selectedCampingStyle={selectedCampingStyle}
            processing={processing}
            setDisabled={setDisabled}
            setProcessing={setProcessing}
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            handleSubmit={handleSubmit}
            notesAndCustomAnswers={notesAndCustomAnswers}
            setNotesAndCustomAnswers={setNotesAndCustomAnswers}
            fromAdParam={fromAdParam}
            setFromAd={setFromAd}
            setIsLoadingIntent={setIsLoadingIntent}
            isPurchaseButtonDisabled={isDisablingError() || processing || disabled || isLoadingCamper}
            errorMessage={props.errorMessage}
            setErrorMessage={props.setErrorMessage}
          />
        </div>
        <div className="flex flex-col gap-2 md:max-w-1/2" style={{ flex: "0 1 50%" }}>
          <div className="text-lg font-bold mb-4">Price Details</div>
          <div>
            <OrderSummary
              order={props.order as IOrder}
              camp={camp}
              isLoading={props.isLoading}
              isPreview
              isIncludingLineItems
              taxRateLabel={taxRateLabel}
              isGuestView
            />
          </div>
          <AddOns
            campingStyle={selectedSiteCampingStyle}
            primaryColor={props.camp.primary_color}
            startDate={props.reservationStartDate}
            endDate={props.reservationEndDate}
            selectedSiteId={props.selectedSite.id}
            groupedReservationId={groupedReservationId}
          />
          {canUseDiscount(props.camp, props.nights) &&
            Object.keys(props.discountRates || {}).length > 0 &&
            !props.priceDetails.flat_rate_price && (
              <DiscountForm
                camp={props.camp}
                discountRates={props.discountRates}
                discountType={props.discountType}
                setDiscountType={props.setDiscountType}
                discountId={props.discountId}
                setDiscountId={props.setDiscountId}
                discountValid={props.discountValid}
                isLoading={props.isLoading}
              />
            )}
          <CouponForm camp={camp} couponsCount={couponsCount} />
        </div>
      </div>
    </div>
  )
}

interface ITaxesAndFeesProps {
  priceDetails: IPriceDetails
  camp: CampProps
  campingStyle: CampingStyleProps
}

export default PriceDetails
